import * as React from 'react'

import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { SitePermitStatus } from '~/client/graph/types.generated'
import ILogisticItem from '~/client/src/shared/models/ILogisticItem'
import ProjectDateStore, {
  HOURS_IN_DAY,
  MILLISECONDS_IN_HOUR,
} from '~/client/src/shared/stores/ui/ProjectDate.store'

import LogisticsGanttStore, {
  LOGISTICS_GANTT_TIMELINE_WIDTH,
} from '../../LogisticsGantt.store'
import LogisticsTimelineArrow from './LogisticsTimelineArrow'

interface IProps {
  logisticItem: ILogisticItem
  logisticsGanttStore: LogisticsGanttStore

  projectDateStore?: ProjectDateStore
}

const DONE_STATUSES: string[] = [SitePermitStatus.Done, SitePermitStatus.Ended]

@inject('projectDateStore')
@observer
export default class LogisticsTimelineHours extends React.Component<IProps> {
  public render() {
    const { timelineColumnHours, currentTimePosition, isNonWorkingHour } =
      this.props.logisticsGanttStore

    return (
      <div className="row full-height full-width relative">
        {timelineColumnHours.map((hour, idx) => (
          <div
            key={hour}
            className={classList({
              'gantt-row full-height flex-item relative': true,
              'br-light-cool-grey': timelineColumnHours.length !== idx + 1,
              'bg-palette-brand-lightest': isNonWorkingHour(hour),
              'bg-white': !isNonWorkingHour(hour),
            })}
          >
            <div className="half-hour-line absolute-top bottom-0" />
          </div>
        ))}
        {currentTimePosition && (
          <div
            className="bl2-secondary-50-dashed absolute-top z-index-10 bottom-0"
            style={{
              left: `${currentTimePosition.pixels}px`,
            }}
          />
        )}
        {this.renderLogisticBarHours()}
      </div>
    )
  }

  private renderLogisticBarHours(): JSX.Element {
    const { logisticItem, projectDateStore, logisticsGanttStore } = this.props
    const { currentDate } = logisticsGanttStore

    if (!logisticItem?.startDate || !logisticItem?.endDate || !currentDate) {
      return null
    }

    const { startDate, endDate } = logisticItem
    const { startOfDay, endOfDay, isSameDay, addDays } = projectDateStore

    const dayStartMs = startOfDay(currentDate).getTime()
    const dayEndMs = endOfDay(currentDate).getTime()

    const itemStartMs = Math.max(startDate, dayStartMs)
    const itemEndMs = Math.min(endDate, dayEndMs)

    if (itemEndMs <= dayStartMs || itemStartMs >= dayEndMs) {
      return null
    }

    const startOffset = this.getHourOffset(itemStartMs, dayStartMs)
    const endOffset = this.getHourOffset(itemEndMs, dayStartMs)

    const pixelsPerHour = LOGISTICS_GANTT_TIMELINE_WIDTH / HOURS_IN_DAY
    const leftPosition = startOffset * pixelsPerHour
    const width = (endOffset - startOffset) * pixelsPerHour

    const isStartDate = startDate >= dayStartMs && startDate <= dayEndMs
    const isEndDate = endDate >= dayStartMs && endDate <= dayEndMs
    const isDayBeforeFinish = isSameDay(currentDate, addDays(endDate, -1))

    const shouldShowLeftArrow = startDate < dayStartMs
    const shouldShowRightArrow = endDate > dayEndMs

    return (
      <div
        className="gantt-hours-bar-container unclickable-element absolute"
        style={{
          left: `${leftPosition}px`,
          width: `${width}px`,
        }}
      >
        <div
          className={classList({
            'row relative z-index-5 full-width gantt-timeline-bar no-grow bt-primary-30 bb-primary-blue': true,
            'start-day': isStartDate,
            'end-day': isEndDate,
            done: DONE_STATUSES.includes(logisticItem.status),
          })}
        >
          {shouldShowLeftArrow && (
            <LogisticsTimelineArrow isLeftArrow status={logisticItem.status} />
          )}
          {shouldShowRightArrow && (
            <LogisticsTimelineArrow
              isLeftArrow={false}
              status={logisticItem.status}
            />
          )}
        </div>
        <div
          className={classList({
            'gantt-timeline-sub-bar no-grow full-width': true,
            'start-day': isStartDate,
            'day-before-finish': isDayBeforeFinish,
            'end-day': isEndDate,
          })}
        />
      </div>
    )
  }

  private getHourOffset = (timestampMs: number, dayStartMs: number): number => {
    const msFromDayStart = timestampMs - dayStartMs
    const hoursFromDayStart = msFromDayStart / MILLISECONDS_IN_HOUR
    return Math.max(0, Math.min(HOURS_IN_DAY, hoursFromDayStart))
  }
}
